/**
 * Standard conditions a user can copy into their own library (owner,
 * 2026-10-26; mock: `docs/conditions-pin-mock.html`).
 *
 * **A preset is copied, never linked.** + Add writes an ordinary saved
 * condition — a fresh id, the preset's category — and from then on it is the
 * user's to rename, star, change or delete; changing the copy never changes the
 * preset, and a later change to a preset never reaches a copy. That is what
 * lets the numbers below be the owner's defaults rather than everybody's rule.
 *
 * Written as expression trees rather than ScanScript text because the parser is
 * server-side (D11) and a preset must work with no compiler injected. `source`
 * carries the text the editor shows; `printExpr` of the tree reads the same.
 *
 * The thresholds are the owner's (2026-10-26): a 2% gap, earnings within 7
 * days, seasonality hit > 80 with an average > 5% over the 30-day window
 * (`seasHit`'s default).
 */

import type { SavedScreenerFilter, ScreenerExpr } from "@qufolio/model";

export interface ConditionPreset {
  id: string;
  name: string;
  /**
   * The group it is listed under, and the category its copy lands in — so a
   * copied "Above MA200" joins a library's existing Moving Averages group
   * rather than a pile of its own.
   */
  category: string;
  /** What the editor shows; the tree below is what runs. */
  source: string;
  filter: ScreenerExpr;
  /**
   * The capability a caller needs to be offered it (`screen:preset:pre-movers`).
   * Absent: everybody. A gated preset is **hidden**, not shown locked (owner,
   * 2026-10-29), and hidden too when the host passes no gate at all.
   */
  requires?: string;
}

/** The host's answer to "may this caller see presets that need `capability`?" */
export type PresetGate = (capability: string) => boolean;

/** Whether `preset` is offered under `allowed`. */
export function presetAllowed(preset: ConditionPreset, allowed?: PresetGate): boolean {
  return !preset.requires || (allowed?.(preset.requires) ?? false);
}

/** The preset groups, in the order the list shows them. */
export const PRESET_CATEGORIES = [
  "Movers",
  "Gaps",
  "Earnings",
  "Growth Boxes",
  "Volume",
  "Highs & Lows",
  "Moving Averages",
  "Bollinger Bands",
  "Patterns",
  "Seasonality",
] as const;

const field = (name: string): ScreenerExpr => ({ kind: "field", field: name });
const num = (value: number): ScreenerExpr => ({ kind: "literal", value });
const bin = (
  op: Extract<ScreenerExpr, { kind: "binary" }>["op"],
  left: ScreenerExpr,
  right: ScreenerExpr,
): ScreenerExpr => ({ kind: "binary", op, left, right });
const call = (fn: string, ...args: ScreenerExpr[]): ScreenerExpr => ({ kind: "call", fn, args });

/**
 * `(a - b) / b * 100` — a percentage move from `b`. Over `prevClose`, the
 * vendor's published previous close, so a gap needs no bar history and is
 * evaluated in the browser rather than on the server.
 */
const pctFrom = (a: ScreenerExpr, b: ScreenerExpr): ScreenerExpr =>
  bin("*", bin("/", bin("-", a, b), b), num(100));

/** `name[offset]` — the field `offset` bars back. */
const back = (name: string, offset: number): ScreenerExpr =>
  offset === 0 ? field(name) : { kind: "field", field: name, ref: { base: name, offset } };

/**
 * A gap of `pct` % within the last `days` bars that price has since traded back
 * through to the close before it (owner, 2026-10-26: 5 days, the 2 % gap).
 *
 * One term per day, ORed: for a gap `k` bars ago, the gap is
 * `open[k]` against `close[k+1]`, and the fill is the low (up) or the high
 * (down) of the `k + 1` bars since reaching that close — the gap day included,
 * so a gap filled the same session counts.
 */
function gapFilled(direction: "up" | "down", days: number, pct: number): ScreenerExpr {
  const terms: ScreenerExpr[] = [];
  for (let k = 0; k < days; k++) {
    const before = back("close", k + 1);
    const gap =
      direction === "up"
        ? bin(">=", pctFrom(back("open", k), before), num(pct))
        : bin(">=", pctFrom(before, back("open", k)), num(pct));
    const fill =
      direction === "up"
        ? bin("<=", call("lowest", field("low"), num(k + 1)), before)
        : bin(">=", call("highest", field("high"), num(k + 1)), before);
    terms.push(bin("and", gap, fill));
  }
  return terms.reduce((a, b) => bin("or", a, b));
}

/** Close within `pct` % of `level`, either side. */
const near = (level: ScreenerExpr, pct: number): ScreenerExpr =>
  bin("<=", call("abs", pctFrom(field("close"), level)), num(pct));

export const CONDITION_PRESETS: readonly ConditionPreset[] = [
  {
    id: "gapUp",
    category: "Gaps",
    name: "Gap Up",
    source: "(open - prevClose) / prevClose * 100 >= 2",
    filter: bin(">=", pctFrom(field("open"), field("prevClose")), num(2)),
  },
  {
    id: "gapDown",
    category: "Gaps",
    name: "Gap Down",
    source: "(prevClose - open) / prevClose * 100 >= 2",
    filter: bin(">=", pctFrom(field("prevClose"), field("open")), num(2)),
  },
  {
    id: "gapUpFilled",
    category: "Gaps",
    name: "Gap Up Filled (5 days)",
    source: "a 2% gap up in the last 5 days, traded back down to the close before it",
    filter: gapFilled("up", 5, 2),
  },
  {
    id: "gapDownFilled",
    category: "Gaps",
    name: "Gap Down Filled (5 days)",
    source: "a 2% gap down in the last 5 days, traded back up to the close before it",
    filter: gapFilled("down", 5, 2),
  },
  {
    id: "earningsSoon",
    category: "Earnings",
    name: "Earnings Soon",
    source: "daysToEarnings >= 1 and daysToEarnings <= 7",
    filter: bin(
      "and",
      bin(">=", field("daysToEarnings"), num(1)),
      bin("<=", field("daysToEarnings"), num(7)),
    ),
  },
  {
    id: "earningsToday",
    category: "Earnings",
    name: "Today's Earnings",
    source: "daysToEarnings == 0",
    filter: bin("==", field("daysToEarnings"), num(0)),
  },
  {
    id: "seasonalityStrong",
    category: "Seasonality",
    name: "Seasonality Hits > 80, Avg > 5%",
    source: "seasHit(30) > 80 and seasEdge(30) > 5",
    filter: bin(
      "and",
      bin(">", call("seasHit", num(30)), num(80)),
      bin(">", call("seasEdge", num(30)), num(5)),
    ),
  },
  {
    // Revenue, not EPS: the screener carries no EPS-surprise column. A
    // fundamentals `percent` metric is stored as a fraction, so 5 % is 0.05.
    // US only, as the metric is.
    id: "revenueSurprise",
    category: "Earnings",
    name: "Revenue Surprise ≥ 5%",
    source: "revSurprise >= 0.05",
    filter: bin(">=", field("revSurprise"), num(0.05)),
  },
  // Bar history from here on: evaluated on the server. The owner's numbers
  // (2026-10-26): 1.5x the 50-day volume, 52 weeks = 252 bars, near = 2 %.
  {
    id: "higherVolume",
    category: "Volume",
    name: "Higher Volume",
    source: "volume > 1.5 * sma(volume, 50)",
    filter: bin(">", field("volume"), bin("*", num(1.5), call("sma", field("volume"), num(50)))),
  },
  {
    id: "newHighs",
    category: "Highs & Lows",
    name: "New Highs",
    source: "high >= highest(high, 252)",
    filter: bin(">=", field("high"), call("highest", field("high"), num(252))),
  },
  {
    // The writer's whole-history column (`ath` in ScanScript), on the high.
    id: "atAth",
    category: "Highs & Lows",
    name: "At ATH",
    source: "high >= ath(high)",
    filter: bin(">=", field("high"), call("ath", field("high"))),
  },
  {
    id: "newLows",
    category: "Highs & Lows",
    name: "New Lows",
    source: "low <= lowest(low, 252)",
    filter: bin("<=", field("low"), call("lowest", field("low"), num(252))),
  },
  {
    id: "nearMa20",
    category: "Moving Averages",
    name: "Near MA20",
    source: "abs((close - sma(close, 20)) / sma(close, 20) * 100) <= 2",
    filter: near(call("sma", field("close"), num(20)), 2),
  },
  {
    id: "nearMa50",
    category: "Moving Averages",
    name: "Near MA50",
    source: "abs((close - sma(close, 50)) / sma(close, 50) * 100) <= 2",
    filter: near(call("sma", field("close"), num(50)), 2),
  },
  {
    // A rolling 20-bar VWAP: on daily bars there is no session to reset at
    // (`indicators.vwap`), so this is not a day trader's intraday VWAP.
    id: "nearVwap",
    category: "Moving Averages",
    name: "Near VWAP",
    source: "abs((close - vwap(close, 20)) / vwap(close, 20) * 100) <= 2",
    filter: near(call("vwap", field("close"), num(20)), 2),
  },
  // Bollinger at its standard 20, 2.
  {
    id: "aboveMa20",
    category: "Moving Averages",
    name: "Above MA20",
    source: "close > sma(close, 20)",
    filter: bin(">", field("close"), call("sma", field("close"), num(20))),
  },
  {
    id: "aboveMa200",
    category: "Moving Averages",
    name: "Above MA200",
    source: "close > sma(close, 200)",
    filter: bin(">", field("close"), call("sma", field("close"), num(200))),
  },
  {
    id: "aboveBollinger",
    category: "Bollinger Bands",
    name: "Above Bollinger",
    source: "close > bbandsUpper(close, 20, 2)",
    filter: bin(">", field("close"), call("bbandsUpper", field("close"), num(20), num(2))),
  },
  {
    id: "belowBollinger",
    category: "Bollinger Bands",
    name: "Below Bollinger",
    source: "close < bbandsLower(close, 20, 2)",
    filter: bin("<", field("close"), call("bbandsLower", field("close"), num(20), num(2))),
  },
];

/**
 * The chart patterns the nightly detector stores per symbol-date
 * (`docs/features/chart-patterns.md`): 0 none, 1 forming, 2 broke out in the
 * last 5 bars (`flatBase` adds −2 for a breakdown). `>= 1` is "forming or a
 * recent breakout", and leaves the breakdown out. Read off the row, so these
 * run in the browser; the fields are behind `ind:Patterns`, and a plan without
 * it gets no rows rather than wrong ones.
 */
const PATTERNS: readonly [id: string, field: string, name: string][] = [
  ["cupHandle", "cupHandle", "Cup and Handle"],
  ["inverseCup", "inverseCup", "Inverse Cup and Handle"],
  ["hnsTop", "hnsTop", "Head and Shoulders Top"],
  ["inverseHns", "inverseHns", "Inverse Head and Shoulders"],
  ["darvasBox", "darvasBox", "Darvas Box"],
  ["flatBase", "flatBase", "Flat Base"],
];

const PATTERN_PRESETS: ConditionPreset[] = PATTERNS.map(([id, name_, label]) => ({
  id: `pattern-${id}`,
  category: "Patterns",
  name: label,
  source: `${name_} >= 1`,
  filter: bin(">=", field(name_), num(1)),
}));

/**
 * The growth boxes, as the source table filters them (owner, 2026-10-29): any
 * box, no box, and each box on its own. `growthBox` is 1-10 with **0 for No
 * Box**, so `> 0` and `= 0` are the source's own two filters; a combination is
 * a copy edited to `growthBox >= 1 and growthBox <= 3`. Read off the row, so
 * these run in the browser; the field is behind `ind:PD`.
 */
const GROWTH_BOX_PRESETS: ConditionPreset[] = [
  {
    id: "growthBoxAny",
    category: "Growth Boxes",
    name: "In a Growth Box",
    source: "growthBox > 0",
    filter: bin(">", field("growthBox"), num(0)),
  },
  {
    id: "growthBoxNone",
    category: "Growth Boxes",
    name: "No Growth Box",
    source: "growthBox == 0",
    filter: bin("==", field("growthBox"), num(0)),
  },
  ...Array.from({ length: 10 }, (_, i): ConditionPreset => ({
    id: `growthBox${i + 1}`,
    category: "Growth Boxes",
    name: `Growth Box ${i + 1}`,
    source: `growthBox == ${i + 1}`,
    filter: bin("==", field("growthBox"), num(i + 1)),
  })),
];

/** Every preset: the hand-written ones, the growth boxes, then the patterns. */
// ── Movers (docs/plans/movers.md) ────────────────────────────────────────────

/** |%Chg| at least this (owner, 2026-10-29: "use .5% . we can change later"). */
export const MOVER_PCT = 0.5;
/** The window's volume at least this, and its last trade at least `MOVER_MIN_LAST`. */
export const MOVER_MIN_VOLUME = 50_000;
export const MOVER_MIN_LAST = 2;

/** One session's columns: its %Chg, its volume, its last trade. */
const MOVER_SESSIONS = [
  { key: "pre", label: "Pre", pct: "prePct", volume: "preVolume", last: "preClose" },
  { key: "post", label: "Post", pct: "postPct", volume: "postVolume", last: "postClose" },
  { key: "market", label: "Market", pct: "changePercentCc", volume: "volume", last: "close" },
] as const;

/**
 * Pre / Post / Market Mover, each with a Gainer and a Loser — nine. The move is
 * `abs(pct) >= 0.5`, or `pct >= 0.5` / `pct <= -0.5`, then the two floors.
 * Gated by the session's preset screen (`screen:preset:<key>-movers`).
 */
export const MOVER_PRESETS: readonly ConditionPreset[] = MOVER_SESSIONS.flatMap((s) => {
  const floors = bin(
    "and",
    bin(">=", field(s.volume), num(MOVER_MIN_VOLUME)),
    bin(">=", field(s.last), num(MOVER_MIN_LAST)),
  );
  const floorText = `${s.volume} >= ${MOVER_MIN_VOLUME} and ${s.last} >= ${MOVER_MIN_LAST}`;
  const variants = [
    { id: "", name: "", move: bin(">=", call("abs", field(s.pct)), num(MOVER_PCT)), text: `abs(${s.pct}) >= ${MOVER_PCT}` },
    { id: "Gainer", name: " Gainer", move: bin(">=", field(s.pct), num(MOVER_PCT)), text: `${s.pct} >= ${MOVER_PCT}` },
    { id: "Loser", name: " Loser", move: bin("<=", field(s.pct), num(-MOVER_PCT)), text: `${s.pct} <= -${MOVER_PCT}` },
  ];
  return variants.map((v) => ({
    id: `${s.key}Mover${v.id}`,
    category: "Movers",
    name: `${s.label} Mover${v.name}`,
    source: `${v.text} and ${floorText}`,
    filter: bin("and", v.move, floors),
    requires: `screen:preset:${s.key}-movers`,
  }));
});

export const ALL_PRESETS: readonly ConditionPreset[] = [
  ...MOVER_PRESETS,
  ...CONDITION_PRESETS,
  ...GROWTH_BOX_PRESETS,
  ...PATTERN_PRESETS,
];

/** The presets `allowed` offers, in list order. */
export function offeredPresets(allowed?: PresetGate): ConditionPreset[] {
  return ALL_PRESETS.filter((p) => presetAllowed(p, allowed));
}

/** The presets grouped for display, in `PRESET_CATEGORIES` order. */
export function presetGroups(allowed?: PresetGate): { category: string; presets: ConditionPreset[] }[] {
  const offered = offeredPresets(allowed);
  return PRESET_CATEGORIES.map((category) => ({
    category,
    presets: offered.filter((p) => p.category === category),
  })).filter((g) => g.presets.length > 0);
}

/**
 * Whether the library already holds this preset's copy.
 *
 * By name within the preset's category: a copy renamed or moved is the user's
 * own condition now, and offering the preset again is right.
 */
export function presetAdded(
  filters: readonly SavedScreenerFilter[],
  preset: ConditionPreset,
): boolean {
  return filters.some((f) => f.name === preset.name && f.category === preset.category);
}

/**
 * The preset as a new saved condition of the user's own.
 *
 * Wrapped in an ALL group, which is the shape the editor edits: a bare
 * expression as the root reads as "no conditions" there, so a copy loaded for
 * editing would look empty.
 */
export function presetCopy(preset: ConditionPreset, id: string): SavedScreenerFilter {
  return {
    id,
    name: preset.name,
    category: preset.category,
    filter: { op: "all", children: [preset.filter] },
  };
}
